// pages/Purchase.js - Corrected Version with Proper Item ID Handling

import { useState, useEffect } from "react";
import { database } from "../firebase/firebase";
import { ref, push, onValue, get, update, set } from "firebase/database";
import { useNavigate, useParams } from "react-router-dom";
import "../styles/main.css";
import BarcodeScanner from "../components/BarcodeScanner";

// Configuration
const FINANCIAL_YEAR_START_MONTH = 3; // April (0-indexed)

export default function Purchase() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  // State for form fields
  const [supplier, setSupplier] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [itemSearch, setItemSearch] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    id: "",
    name: "",
    qty: 1,
    price: 0,
    gst: 0,
    discount: 0,
    hsn: "",
    applyTax: true
  });
  const [notes, setNotes] = useState("");
  const [purchaseNo, setPurchaseNo] = useState("");
  const [manualPurchaseNo, setManualPurchaseNo] = useState("");
  const [purchaseNumber, setPurchaseNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [financialYear, setFinancialYear] = useState("");
  const [purchaseNumberError, setPurchaseNumberError] = useState("");
  const [existingPurchaseNumbers, setExistingPurchaseNumbers] = useState(new Set());
  const [isInitialized, setIsInitialized] = useState(false);
  const [applyTaxGlobally, setApplyTaxGlobally] = useState(true);
  
  // Date state
  const [purchaseDate, setPurchaseDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  
  // Supplier Invoice Details
  const [supplierInvoiceNo, setSupplierInvoiceNo] = useState("");
  const [supplierInvoiceDate, setSupplierInvoiceDate] = useState("");

  // Company details from Firebase
  const [companyDetails, setCompanyDetails] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    gstin: "",
    website: "",
    logo: "",
    state: "",
    stateCode: "",
    terms: "Thank you for your business!"
  });

  // Settings from Firebase
  const [settings, setSettings] = useState({
    financialYear: "",
    pdfShareOption: "share",
    printFormat: "standard",
    purchaseFormat: "standard",
    billSize: "a4",
    purchasePrefix: "PO-",
    purchaseSuffix: "",
    purchaseStartingNumber: 1,
    allowManualPurchaseNumber: true,
    autoGeneratePurchaseNumber: false
  });

  // Utility function to get current financial year
  const getFinancialYear = (date = new Date()) => {
    if (settings.financialYear && settings.financialYear !== "") {
      return settings.financialYear;
    }
    const year = date.getFullYear();
    const month = date.getMonth();
    if (month < FINANCIAL_YEAR_START_MONTH) {
      return `${year-1}-${year}`;
    }
    return `${year}-${year+1}`;
  };

  // Update financial year
  useEffect(() => {
    if (settings.financialYear && settings.financialYear !== "") {
      setFinancialYear(settings.financialYear);
    } else {
      const selectedDate = new Date(purchaseDate);
      const fy = getFinancialYear(selectedDate);
      setFinancialYear(fy);
    }
  }, [purchaseDate, settings.financialYear]);

  // Load existing purchase numbers
  const loadExistingPurchaseNumbers = async (fy) => {
    try {
      const currentFY = fy || settings.financialYear || getFinancialYear(new Date(purchaseDate));
      const financialYearsRef = ref(database, `financialYears/${currentFY}/purchases`);
      const snapshot = await get(financialYearsRef);
      const numbers = new Set();
      let lastNum = 0;
      
      if (snapshot.exists()) {
        const purchases = snapshot.val();
        Object.values(purchases).forEach(purchase => {
          if (purchase.purchaseNo) {
            numbers.add(purchase.purchaseNo);
            const match = purchase.purchaseNo.match(/\d+/);
            if (match) {
              const num = parseInt(match[0], 10);
              if (!isNaN(num) && num > lastNum) lastNum = num;
            }
          }
        });
      }
      
      setExistingPurchaseNumbers(numbers);
      return { numbers, lastNumber: lastNum };
    } catch (error) {
      console.error("Error loading purchase numbers:", error);
      return { numbers: new Set(), lastNumber: 0 };
    }
  };

  // Generate purchase number
  const generatePurchaseNumber = async (fy, forceGenerate = false) => {
    if (!settings.autoGeneratePurchaseNumber && !forceGenerate) {
      return;
    }
    
    try {
      const currentFY = fy || settings.financialYear || getFinancialYear(new Date(purchaseDate));
      const prefix = settings.purchasePrefix || 'PO-';
      const { lastNumber } = await loadExistingPurchaseNumbers(currentFY);
      
      let newNumber;
      if (lastNumber > 0) {
        newNumber = lastNumber + 1;
      } else {
        newNumber = 1;
      }
      
      if (!isEditMode || forceGenerate) {
        setPurchaseNumber(newNumber);
        const newPurchaseNo = `${prefix}${newNumber}`;
        setPurchaseNo(newPurchaseNo);
        setManualPurchaseNo(newPurchaseNo);
        setPurchaseNumberError("");
      }
      
      return newNumber;
    } catch (error) {
      console.error("Error generating purchase number:", error);
      const fallbackNumber = 1;
      if (!isEditMode || forceGenerate) {
        setPurchaseNumber(fallbackNumber);
        setPurchaseNo(`${settings.purchasePrefix || 'PO-'}${fallbackNumber}`);
      }
      return fallbackNumber;
    }
  };

  const isPurchaseNumberDuplicate = (number) => {
    if (isEditMode) {
      return existingPurchaseNumbers.has(number) && number !== purchaseNo;
    }
    return existingPurchaseNumbers.has(number);
  };

  const handleManualPurchaseNoChange = (e) => {
    const value = e.target.value;
    setManualPurchaseNo(value);
    setPurchaseNo(value);
    
    const match = value.match(/\d+/);
    if (match) {
      setPurchaseNumber(parseInt(match[0], 10));
    }
    
    if (isPurchaseNumberDuplicate(value)) {
      setPurchaseNumberError("This purchase number already exists. Please use a different number.");
    } else {
      setPurchaseNumberError("");
    }
  };

  const handleBarcodeScan = (barcode) => {
    const foundItem = items.find(item => item.barcode === barcode || item.code === barcode);
    if (foundItem) {
      addItemWithDetails({
        id: foundItem.id,
        name: foundItem.name,
        qty: 1,
        price: Number(foundItem.purchasePrice || foundItem.price || 0),
        gst: Number(foundItem.gst || 0),
        discount: 0,
        hsn: foundItem.hsn || "",
        applyTax: applyTaxGlobally
      });
      setShowScanner(false);
    } else {
      alert("Item not found with this barcode");
    }
  };

  const handleItemSelect = (item) => {
    if (item) {
      addItemWithDetails({
        id: item.id,
        name: item.name,
        qty: 1,
        price: Number(item.purchasePrice || item.price || 0),
        gst: Number(item.gst || 0),
        discount: 0,
        hsn: item.hsn || "",
        applyTax: applyTaxGlobally
      });
      setItemSearch("");
      setShowItemDropdown(false);
    }
  };

  // FIXED: Use actual item ID and handle duplicate items
  const addItemWithDetails = (itemDetails) => {
    const qty = Number(itemDetails.qty) || 1;
    const price = Number(itemDetails.price) || 0;
    const gst = Number(itemDetails.gst) || 0;
    const discount = Number(itemDetails.discount) || 0;
    const applyTax = itemDetails.applyTax !== undefined ? itemDetails.applyTax : applyTaxGlobally;

    const itemTotal = qty * price;
    const discountAmount = itemTotal * (discount / 100);
    const taxableValue = itemTotal - discountAmount;
    
    let gstDetails = { cgst: 0, sgst: 0, total: 0 };
    let netTotal = taxableValue;
    
    if (applyTax && gst > 0) {
      const gstAmount = taxableValue * (gst / 100);
      gstDetails = {
        cgst: gstAmount / 2,
        sgst: gstAmount / 2,
        total: gstAmount
      };
      netTotal = taxableValue + gstAmount;
    }

    // Check if item already exists in purchase items
    const existingItemIndex = purchaseItems.findIndex(item => item.id === itemDetails.id);
    
    if (existingItemIndex >= 0) {
      // Update existing item quantity
      const updatedItems = [...purchaseItems];
      const existingItem = updatedItems[existingItemIndex];
      const newQty = existingItem.qty + qty;
      
      const newItemTotal = newQty * price;
      const newDiscountAmount = newItemTotal * (discount / 100);
      const newTaxableValue = newItemTotal - newDiscountAmount;
      
      let newGstDetails = { cgst: 0, sgst: 0, total: 0 };
      let newNetTotal = newTaxableValue;
      
      if (applyTax && gst > 0) {
        const gstAmount = newTaxableValue * (gst / 100);
        newGstDetails = {
          cgst: gstAmount / 2,
          sgst: gstAmount / 2,
          total: gstAmount
        };
        newNetTotal = newTaxableValue + gstAmount;
      }
      
      updatedItems[existingItemIndex] = {
        ...existingItem,
        qty: newQty,
        itemTotal: newItemTotal,
        discountAmount: newDiscountAmount,
        taxableValue: newTaxableValue,
        gstDetails: newGstDetails,
        netTotal: newNetTotal
      };
      
      setPurchaseItems(updatedItems);
    } else {
      // Add new item - use the actual item ID from database
      setPurchaseItems([
        ...purchaseItems,
        {
          ...itemDetails,
          qty,
          price,
          gst,
          discount,
          applyTax,
          itemTotal,
          discountAmount,
          taxableValue,
          gstDetails,
          netTotal,
          id: itemDetails.id, // Use actual item ID, not Date.now()
          itemId: itemDetails.id
        }
      ]);
    }
  };

  const removeItem = (itemId) => {
    setPurchaseItems(purchaseItems.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId, newQty) => {
    if (newQty < 1) return;
    
    setPurchaseItems(purchaseItems.map(item => {
      if (item.id === itemId) {
        const qty = Number(newQty) || 1;
        const price = Number(item.price) || 0;
        const gst = Number(item.gst) || 0;
        const discount = Number(item.discount) || 0;
        const applyTax = item.applyTax;
        
        const itemTotal = qty * price;
        const discountAmount = itemTotal * (discount / 100);
        const taxableValue = itemTotal - discountAmount;
        
        let gstDetails = { cgst: 0, sgst: 0, total: 0 };
        let netTotal = taxableValue;
        
        if (applyTax && gst > 0) {
          const gstAmount = taxableValue * (gst / 100);
          gstDetails = {
            cgst: gstAmount / 2,
            sgst: gstAmount / 2,
            total: gstAmount
          };
          netTotal = taxableValue + gstAmount;
        }
        
        return {
          ...item,
          qty,
          itemTotal,
          discountAmount,
          taxableValue,
          gstDetails,
          netTotal
        };
      }
      return item;
    }));
  };

  const updatePrice = (itemId, newPrice) => {
    setPurchaseItems(purchaseItems.map(item => {
      if (item.id === itemId) {
        const qty = Number(item.qty) || 1;
        const price = Number(newPrice) || 0;
        const gst = Number(item.gst) || 0;
        const discount = Number(item.discount) || 0;
        const applyTax = item.applyTax;
        
        const itemTotal = qty * price;
        const discountAmount = itemTotal * (discount / 100);
        const taxableValue = itemTotal - discountAmount;
        
        let gstDetails = { cgst: 0, sgst: 0, total: 0 };
        let netTotal = taxableValue;
        
        if (applyTax && gst > 0) {
          const gstAmount = taxableValue * (gst / 100);
          gstDetails = {
            cgst: gstAmount / 2,
            sgst: gstAmount / 2,
            total: gstAmount
          };
          netTotal = taxableValue + gstAmount;
        }
        
        return {
          ...item,
          price,
          itemTotal,
          discountAmount,
          taxableValue,
          gstDetails,
          netTotal
        };
      }
      return item;
    }));
  };

  const updateDiscount = (itemId, discount, discountType) => {
    setPurchaseItems(purchaseItems.map(item => {
      if (item.id === itemId) {
        const qty = Number(item.qty) || 1;
        const price = Number(item.price) || 0;
        const gst = Number(item.gst) || 0;
        const newDiscount = Number(discount) || 0;
        const applyTax = item.applyTax;
        
        const itemTotal = qty * price;
        let discountAmount = 0;
        if (discountType === "percentage") {
          discountAmount = itemTotal * (newDiscount / 100);
        } else {
          discountAmount = newDiscount;
        }
        const taxableValue = itemTotal - discountAmount;
        
        let gstDetails = { cgst: 0, sgst: 0, total: 0 };
        let netTotal = taxableValue;
        
        if (applyTax && gst > 0) {
          const gstAmount = taxableValue * (gst / 100);
          gstDetails = {
            cgst: gstAmount / 2,
            sgst: gstAmount / 2,
            total: gstAmount
          };
          netTotal = taxableValue + gstAmount;
        }
        
        return {
          ...item,
          discount: newDiscount,
          discountType,
          discountAmount,
          taxableValue,
          gstDetails,
          netTotal
        };
      }
      return item;
    }));
  };

  const calculateTotals = () => {
    let taxableValue = 0;
    let cgst = 0;
    let sgst = 0;
    let totalGST = 0;
    let totalDiscount = 0;
    let grandTotal = 0;

    purchaseItems.forEach(item => {
      taxableValue += Number(item.taxableValue) || 0;
      cgst += Number(item.gstDetails?.cgst) || 0;
      sgst += Number(item.gstDetails?.sgst) || 0;
      totalGST += Number(item.gstDetails?.total) || 0;
      totalDiscount += Number(item.discountAmount) || 0;
      grandTotal += Number(item.netTotal) || 0;
    });

    return {
      taxableValue,
      cgst,
      sgst,
      totalGST,
      totalDiscount,
      grandTotal
    };
  };

  const filteredSuppliers = suppliers.filter(s => 
    (s.masterType === "sundryCreditor") && 
    (s.name?.toLowerCase().includes(supplier.toLowerCase()) ||
    s.mobile?.includes(supplier) ||
    s.gstin?.toLowerCase().includes(supplier.toLowerCase()))
  );

  const filteredItems = items.filter(item => 
    item.name?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    item.hsn?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    item.barcode?.includes(itemSearch) ||
    item.code?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  const selectSupplier = (sup) => {
    setSelectedSupplier(sup);
    setSupplier(sup.name);
    setSupplierId(sup.id);
    setShowSupplierDropdown(false);
  };

  // Update stock function - ADDS stock when purchasing
  const updateStock = async (itemId, quantity) => {
    try {
      const itemRef = ref(database, `items/${itemId}`);
      const itemSnapshot = await get(itemRef);
      
      if (itemSnapshot.exists()) {
        const item = itemSnapshot.val();
        const currentStock = item.currentStock || 0;
        const newStock = currentStock + quantity;
        
        await update(itemRef, {
          currentStock: newStock,
          lastPurchaseDate: purchaseDate,
          lastPurchaseQuantity: quantity,
          lastStockUpdate: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        
        console.log(`📦 Stock INCREASED for ${item.name}: ${currentStock} → ${newStock} (+${quantity})`);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating stock:", error);
      throw error;
    }
  };

  const savePurchase = async () => {
    if (!selectedSupplier) {
      alert("Please select a supplier");
      return;
    }

    if (purchaseItems.length === 0) {
      alert("Please add at least one item");
      return;
    }

    if (purchaseNo && isPurchaseNumberDuplicate(purchaseNo)) {
      alert("This purchase number already exists. Please use a different number.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const timestamp = new Date().toISOString();
      const totals = calculateTotals();
      const fy = settings.financialYear || getFinancialYear(new Date(purchaseDate));
      
      const purchaseData = {
        purchaseNo: purchaseNo || "",
        supplierInvoiceNo: supplierInvoiceNo || "",
        supplierInvoiceDate: supplierInvoiceDate || "",
        financialYear: fy,
        applyTaxGlobally: applyTaxGlobally,
        companyDetails: {
          name: companyDetails?.name || "",
          address: companyDetails?.address || "",
          phone: companyDetails?.phone || "",
          email: companyDetails?.email || "",
          gstin: companyDetails?.gstin || "",
          website: companyDetails?.website || "",
          state: companyDetails?.state || "",
          stateCode: companyDetails?.stateCode || ""
        },
        supplierId: selectedSupplier?.id || "",
        supplierName: selectedSupplier?.name || "",
        supplierMobile: selectedSupplier?.mobile || "",
        supplierGST: selectedSupplier?.gstin || "",
        supplierAddress: selectedSupplier?.address || "",
        supplierState: selectedSupplier?.state || "",
        supplierStateCode: selectedSupplier?.stateCode || "",
        items: purchaseItems.map(item => ({
          itemId: item?.id || "",
          name: item?.name || "",
          hsn: item?.hsn || "",
          qty: Number(item?.qty) || 0,
          price: Number(item?.price) || 0,
          gst: Number(item?.gst) || 0,
          discount: Number(item?.discount) || 0,
          discountType: item?.discountType || "percentage",
          applyTax: item?.applyTax !== undefined ? item.applyTax : true,
          itemTotal: Number(item?.itemTotal) || 0,
          discountAmount: Number(item?.discountAmount) || 0,
          taxableValue: Number(item?.taxableValue) || 0,
          gstDetails: {
            cgst: Number(item?.gstDetails?.cgst) || 0,
            sgst: Number(item?.gstDetails?.sgst) || 0,
            total: Number(item?.gstDetails?.total) || 0
          },
          netTotal: Number(item?.netTotal) || 0
        })),
        taxableValue: Number(totals.taxableValue) || 0,
        cgst: Number(totals.cgst) || 0,
        sgst: Number(totals.sgst) || 0,
        totalGST: Number(totals.totalGST) || 0,
        totalDiscount: Number(totals.totalDiscount) || 0,
        grandTotal: Number(totals.grandTotal) || 0,
        notes: notes || "",
        date: purchaseDate || new Date().toISOString().split('T')[0],
        createdAt: isEditMode ? null : timestamp,
        updatedAt: timestamp,
        status: "completed",
        billSize: settings?.billSize || "a4",
        printFormat: settings?.printFormat || "standard"
      };

      const cleanData = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(item => cleanData(item));
        } else if (obj && typeof obj === 'object') {
          return Object.keys(obj).reduce((acc, key) => {
            const value = obj[key];
            if (value !== undefined && value !== null) {
              if (typeof value === 'object') {
                acc[key] = cleanData(value);
              } else {
                acc[key] = value;
              }
            }
            return acc;
          }, {});
        }
        return obj;
      };

      const cleanedPurchaseData = cleanData(purchaseData);

      // Save purchase and update stock
      if (isEditMode) {
        const financialYearsRef = ref(database, "financialYears");
        const snapshot = await get(financialYearsRef);
        
        if (snapshot.exists()) {
          const years = snapshot.val();
          let foundYear = null;
          
          for (const [year, yearData] of Object.entries(years)) {
            if (yearData.purchases && yearData.purchases[id]) {
              foundYear = year;
              break;
            }
          }
          
          if (foundYear) {
            const purchaseRef = ref(database, `financialYears/${foundYear}/purchases/${id}`);
            await update(purchaseRef, {
              ...cleanedPurchaseData,
              updatedAt: timestamp
            });
          } else {
            const purchasesRef = ref(database, `financialYears/${fy}/purchases`);
            const purchaseRef = ref(database, `financialYears/${fy}/purchases/${id}`);
            await set(purchaseRef, {
              ...cleanedPurchaseData,
              updatedAt: timestamp
            });
          }
        }
        alert(`Purchase ${purchaseNo} updated successfully!`);
        setTimeout(() => {
          navigate("/PurchaseList");
        }, 1500);
      } else {
        const financialYearRef = ref(database, `financialYears/${fy}`);
        const fySnapshot = await get(financialYearRef);
        
        if (!fySnapshot.exists()) {
          await set(financialYearRef, {
            purchases: {},
            purchaseSummary: {
              totalPurchases: 0,
              totalAmount: 0,
              totalTax: 0,
              lastUpdated: timestamp
            }
          });
        } else {
          const purchasesRef = ref(database, `financialYears/${fy}/purchases`);
          const purchasesSnapshot = await get(purchasesRef);
          if (!purchasesSnapshot.exists()) {
            await set(purchasesRef, {});
          }
        }
        
        const purchasesRef = ref(database, `financialYears/${fy}/purchases`);
        await push(purchasesRef, cleanedPurchaseData);
        
        // Update stock for each purchased item using actual item ID
        for (const item of purchaseItems) {
          await updateStock(item.id, item.qty);
        }
        
        const summaryRef = ref(database, `financialYears/${fy}/purchaseSummary`);
        const summarySnapshot = await get(summaryRef);
        const currentSummary = summarySnapshot.exists() ? summarySnapshot.val() : {
          totalPurchases: 0,
          totalAmount: 0,
          totalTax: 0
        };
        
        await set(summaryRef, {
          totalPurchases: (currentSummary.totalPurchases || 0) + 1,
          totalAmount: (currentSummary.totalAmount || 0) + (purchaseData.grandTotal || 0),
          totalTax: (currentSummary.totalTax || 0) + (purchaseData.totalGST || 0),
          lastUpdated: timestamp
        });
        
        alert(`Purchase ${purchaseNo || "order"} saved successfully! Stock has been updated.`);
        resetForm();
        setLoading(false);
      }

    } catch (error) {
      console.error("Error saving purchase:", error);
      setError(error.message || "Failed to save purchase");
      alert(`Failed to save purchase: ${error.message}`);
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedSupplier(null);
    setSupplier("");
    setSupplierId("");
    setPurchaseItems([]);
    setNotes("");
    setSupplierInvoiceNo("");
    setSupplierInvoiceDate("");
    setPurchaseNumberError("");
    setApplyTaxGlobally(true);
    if (settings.autoGeneratePurchaseNumber) {
      generatePurchaseNumber(settings.financialYear || getFinancialYear(new Date(purchaseDate)), true);
    } else {
      setPurchaseNo("");
      setManualPurchaseNo("");
    }
  };

  // Fetch data from Firebase
  useEffect(() => {
    const initializeData = async () => {
      const settingsRef = ref(database, "settings");
      const settingsSnapshot = await get(settingsRef);
      let settingsData = {};
      
      if (settingsSnapshot.exists()) {
        settingsData = settingsSnapshot.val();
        setSettings(prev => ({
          ...prev,
          ...settingsData,
          financialYear: settingsData.financialYear || "2025-26",
          billSize: settingsData.billSize || settingsData.printSize || "a4",
          printFormat: settingsData.printFormat || "standard",
          purchasePrefix: settingsData.purchasePrefix || "PO-",
          purchaseStartingNumber: settingsData.purchaseStartingNumber || 1,
          allowManualPurchaseNumber: settingsData.allowManualPurchaseNumber !== false,
          autoGeneratePurchaseNumber: settingsData.autoGeneratePurchaseNumber || false
        }));
      }

      const companyRef = ref(database, "company");
      onValue(companyRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setCompanyDetails(prev => ({ ...prev, ...data }));
        }
      });

      const itemsRef = ref(database, "items");
      onValue(itemsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const itemList = Object.entries(data).map(([id, item]) => ({
            id,
            ...item,
            purchasePrice: Number(item.purchasePrice) || Number(item.price) || 0,
            sellingPrice: Number(item.sellingPrice) || 0,
            price: Number(item.price) || 0,
            gst: Number(item.gst) || 0,
            hsn: item.hsn || "",
            name: item.name || "",
            barcode: item.barcode || "",
            code: item.code || "",
            currentStock: item.currentStock || 0
          }));
          setItems(itemList);
        }
      });

      const customersRef = ref(database, "customers");
      onValue(customersRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const supplierList = Object.entries(data)
            .map(([id, customer]) => ({
              id,
              ...customer,
              name: customer.name,
              mobile: customer.mobile,
              gstin: customer.gstin || customer.gst,
              address: customer.address,
              state: customer.state,
              stateCode: customer.stateCode,
              masterType: customer.masterType || "sundryDebtor"
            }))
            .filter(customer => customer.masterType === "sundryCreditor");
          
          setSuppliers(supplierList);
        } else {
          setSuppliers([]);
        }
      });

      if (isEditMode) {
        await loadPurchaseForEdit();
      } else {
        const currentFY = settingsData.financialYear || getFinancialYear(new Date(purchaseDate));
        await loadExistingPurchaseNumbers(currentFY);
        if (settingsData.autoGeneratePurchaseNumber) {
          await generatePurchaseNumber(currentFY, true);
        } else {
          setPurchaseNo("");
          setManualPurchaseNo("");
        }
      }
      
      setIsInitialized(true);
    };

    const loadPurchaseForEdit = async () => {
      try {
        const financialYearsRef = ref(database, "financialYears");
        const snapshot = await get(financialYearsRef);
        
        if (snapshot.exists()) {
          const years = snapshot.val();
          let foundPurchase = null;
          let foundYear = null;
          
          for (const [year, yearData] of Object.entries(years)) {
            if (yearData.purchases && yearData.purchases[id]) {
              foundPurchase = yearData.purchases[id];
              foundYear = year;
              break;
            }
          }
          
          if (foundPurchase) {
            setPurchaseNo(foundPurchase.purchaseNo || "");
            setManualPurchaseNo(foundPurchase.purchaseNo || "");
            setSupplierInvoiceNo(foundPurchase.supplierInvoiceNo || "");
            setSupplierInvoiceDate(foundPurchase.supplierInvoiceDate || "");
            
            const match = foundPurchase.purchaseNo?.match(/\d+/);
            if (match) {
              setPurchaseNumber(parseInt(match[0], 10));
            }
            
            setPurchaseDate(foundPurchase.date || new Date().toISOString().split('T')[0]);
            setFinancialYear(foundYear || getFinancialYear(new Date(foundPurchase.date)));
            
            if (foundPurchase.supplierId) {
              const supplierData = {
                id: foundPurchase.supplierId,
                name: foundPurchase.supplierName || "",
                mobile: foundPurchase.supplierMobile || "",
                gstin: foundPurchase.supplierGST || "",
                address: foundPurchase.supplierAddress || "",
                state: foundPurchase.supplierState || "",
                stateCode: foundPurchase.supplierStateCode || "",
                masterType: "sundryCreditor"
              };
              setSelectedSupplier(supplierData);
              setSupplier(supplierData.name);
              setSupplierId(supplierData.id);
            }
            
            if (foundPurchase.items && Array.isArray(foundPurchase.items)) {
              setPurchaseItems(foundPurchase.items);
            }
            
            setNotes(foundPurchase.notes || "");
            setApplyTaxGlobally(foundPurchase.applyTaxGlobally !== undefined ? foundPurchase.applyTaxGlobally : true);
            
            await loadExistingPurchaseNumbers(foundYear || foundPurchase.financialYear);
          }
        }
      } catch (error) {
        console.error("Error loading purchase:", error);
        setError("Failed to load purchase");
      }
    };

    initializeData();
  }, []);

  const totals = calculateTotals();
  const displayFinancialYear = settings.financialYear || getFinancialYear(new Date(purchaseDate));

  return (
    <div className="purchase-page">
      {/* Header */}
      <div className="page-header">
        <h2>{isEditMode ? "Edit Purchase" : "Create Purchase Order"}</h2>
        <div className="header-actions">
          <button onClick={() => navigate("/PurchaseList")} className="back-btn">
            ← Purchases
          </button>
        </div>
      </div>

      {/* Financial Year Bar */}
      <div className="financial-year-bar">
        <span>Financial Year:</span>
        <strong>{financialYear || displayFinancialYear}</strong>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="error-alert">
          <span>⚠️</span>
          <p>{error}</p>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="purchase-container">
        {/* LEFT COLUMN - FORM SECTION */}
        <div className="form-section">
          {/* Purchase Number Card */}
          <div className="card">
            <h3>Purchase Order Number</h3>
            {settings.autoGeneratePurchaseNumber ? (
              <div>
                <div className="invoice-display">
                  <span>{settings.purchasePrefix || 'PO-'}</span>
                  <input
                    type="number"
                    value={purchaseNumber}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setPurchaseNumber(val);
                      setPurchaseNo(`${settings.purchasePrefix || 'PO-'}${val}`);
                      setManualPurchaseNo(`${settings.purchasePrefix || 'PO-'}${val}`);
                    }}
                    min="1"
                  />
                </div>
                <p className="hint">Auto-generated purchase number for {financialYear || displayFinancialYear}</p>
              </div>
            ) : (
              <div>
                <div className="input-group">
                  <input
                    type="text"
                    value={manualPurchaseNo}
                    onChange={handleManualPurchaseNoChange}
                    placeholder="Enter purchase order number"
                    className={purchaseNumberError ? 'error' : ''}
                  />
                </div>
                {purchaseNumberError && <p className="error-message">{purchaseNumberError}</p>}
                <p className="hint">Enter your internal purchase order number (optional)</p>
              </div>
            )}
          </div>

          {/* Supplier Invoice Details Card */}
          <div className="card">
            <h3>Supplier Invoice Details</h3>
            <input
              type="text"
              placeholder="Supplier Invoice Number"
              value={supplierInvoiceNo}
              onChange={(e) => setSupplierInvoiceNo(e.target.value)}
              className="date-input"
              style={{ marginBottom: "10px" }}
            />
            <input
              type="date"
              placeholder="Supplier Invoice Date"
              value={supplierInvoiceDate}
              onChange={(e) => setSupplierInvoiceDate(e.target.value)}
              className="date-input"
            />
            <p className="hint">Enter the invoice number and date from the supplier's bill</p>
          </div>

          {/* Purchase Date Card */}
          <div className="card">
            <h3>Purchase Date</h3>
            <input
              type="date"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              className="date-input"
            />
          </div>

          {/* Supplier Card */}
          <div className="card">
            <h3>Supplier Details</h3>
            <div className="supplier-selector">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search supplier by name or mobile..."
                  value={supplier}
                  onChange={(e) => {
                    setSupplier(e.target.value);
                    setShowSupplierDropdown(true);
                    setSelectedSupplier(null);
                  }}
                  onFocus={() => setShowSupplierDropdown(true)}
                />
              </div>

              {showSupplierDropdown && supplier && filteredSuppliers.length > 0 && (
                <div className="dropdown">
                  {filteredSuppliers.map(sup => (
                    <div key={sup.id} className="dropdown-item" onClick={() => selectSupplier(sup)}>
                      <div>
                        <strong>{sup.name}</strong>
                        {sup.mobile && <span className="mobile"> ({sup.mobile})</span>}
                      </div>
                      <div className="supplier-type-badge">Supplier</div>
                    </div>
                  ))}
                </div>
              )}

              {selectedSupplier && (
                <div className="selected-supplier">
                  <div className="supplier-header">
                    <strong>{selectedSupplier.name}</strong>
                    <button className="change-btn" onClick={() => {
                      setSelectedSupplier(null);
                      setSupplier("");
                    }}>Change</button>
                  </div>
                  {selectedSupplier.mobile && <div>📱 {selectedSupplier.mobile}</div>}
                  {selectedSupplier.gstin && <div>GST: {selectedSupplier.gstin}</div>}
                  {selectedSupplier.address && <div>📍 {selectedSupplier.address}</div>}
                </div>
              )}

              <button 
                className="add-supplier-btn"
                onClick={() => navigate("/customers")}
                style={{
                  marginTop: "10px",
                  padding: "8px",
                  background: "#f0f0f0",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  cursor: "pointer",
                  width: "100%",
                  fontSize: "12px"
                }}
              >
                + Add New Supplier (as Sundry Creditor)
              </button>
            </div>
          </div>

          {/* Tax Settings Card */}
          <div className="card">
            <div className="card-header">
              <h3>Tax Settings</h3>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={applyTaxGlobally}
                  onChange={(e) => setApplyTaxGlobally(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p className="hint">
              {applyTaxGlobally ? "GST will be applied to items" : "No GST will be applied to any item"}
            </p>
          </div>

          {/* Add Items Card */}
          <div className="card">
            <h3>Add Items</h3>
            <div className="item-selector">
              <div className="search-row">
                <input
                  type="text"
                  placeholder="Search items by name, HSN or barcode..."
                  value={itemSearch}
                  onChange={(e) => {
                    setItemSearch(e.target.value);
                    setShowItemDropdown(true);
                  }}
                  onFocus={() => setShowItemDropdown(true)}
                />
                <button className="scan-btn" onClick={() => setShowScanner(true)}>
                  📷 Scan
                </button>
              </div>

              {showScanner && (
                <BarcodeScanner 
                  onScan={handleBarcodeScan}
                  onClose={() => setShowScanner(false)}
                />
              )}

              {showItemDropdown && itemSearch && filteredItems.length > 0 && (
                <div className="item-dropdown">
                  {filteredItems.map(item => (
                    <div key={item.id} className="item-option" onClick={() => handleItemSelect(item)}>
                      <div className="item-header">
                        <strong>{item.name}</strong>
                        <span>₹{Number(item.purchasePrice || item.price || 0).toFixed(2)}</span>
                      </div>
                      <div className="item-details">
                        {item.hsn && <span>HSN: {item.hsn}</span>}
                        {item.gst > 0 && <span>{item.gst}% GST</span>}
                        {item.currentStock !== undefined && <span>📦 Stock: {item.currentStock}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button 
                className="add-item-btn"
                onClick={() => navigate("/items")}
                style={{
                  marginTop: "10px",
                  padding: "8px",
                  background: "#f0f0f0",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  cursor: "pointer",
                  width: "100%",
                  fontSize: "12px"
                }}
              >
                + Add New Item
              </button>
            </div>
          </div>

          {/* Notes Card */}
          <div className="card">
            <h3>Notes (Optional)</h3>
            <textarea
              className="notes-input"
              placeholder="Add any additional notes here..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="3"
            />
          </div>
        </div>

        {/* RIGHT COLUMN - PURCHASE ITEMS */}
        <div className="items-section">
          <div className="card items-card">
            <h3>Purchase Items</h3>
            
            {purchaseItems.length > 0 ? (
              <>
                <div className="items-list">
                  {purchaseItems.map((item) => (
                    <div key={item.id} className="item-row">
                      <div className="item-row-header">
                        <div className="item-info">
                          <span className="item-name">{item.name}</span>
                          {item.hsn && <span className="item-hsn">HSN: {item.hsn}</span>}
                        </div>
                        <div className="item-actions">
                          <button className="remove-btn" onClick={() => removeItem(item.id)}>✕</button>
                        </div>
                      </div>
                      
                      <div className="item-row-details">
                        <div className="quantity-price">
                          <span>₹</span>
                          <input
                            type="number"
                            value={item.price}
                            onChange={(e) => updatePrice(item.id, parseFloat(e.target.value) || 0)}
                            className="price-input"
                            step="1"
                          />
                          <span> × </span>
                          <input
                            type="number"
                            min="1"
                            value={item.qty}
                            onChange={(e) => updateQuantity(item.id, parseFloat(e.target.value) || 1)}
                            className="qty-input"
                          />
                        </div>
                        
                        <div className="item-totals">
                          <input
                            type="number"
                            value={item.discount}
                            onChange={(e) => updateDiscount(item.id, parseFloat(e.target.value) || 0, item.discountType || "percentage")}
                            className="discount-input"
                            placeholder="Disc %"
                            step="1"
                          />
                          <select
                            value={item.discountType || "percentage"}
                            onChange={(e) => updateDiscount(item.id, item.discount, e.target.value)}
                            className="discount-type"
                          >
                            <option value="percentage">%</option>
                            <option value="fixed">₹</option>
                          </select>
                          <span className="net-total">₹{Number(item.netTotal).toFixed(2)}</span>
                        </div>
                      </div>

                      {item.applyTax && Number(item.gst) > 0 && (
                        <div className="gst-breakup">
                          <span>CGST: ₹{Number(item.gstDetails?.cgst).toFixed(2)}</span>
                          <span>SGST: ₹{Number(item.gstDetails?.sgst).toFixed(2)}</span>
                          <span className="gst-rate">{item.gst}% GST</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="purchase-summary">
                  <div className="summary-row">
                    <span>Taxable Value:</span>
                    <span>₹{totals.taxableValue.toFixed(2)}</span>
                  </div>
                  
                  {totals.cgst > 0 && (
                    <div className="summary-row">
                      <span>CGST:</span>
                      <span>₹{totals.cgst.toFixed(2)}</span>
                    </div>
                  )}
                  {totals.sgst > 0 && (
                    <div className="summary-row">
                      <span>SGST:</span>
                      <span>₹{totals.sgst.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {totals.totalDiscount > 0 && (
                    <div className="summary-row discount">
                      <span>Total Discount:</span>
                      <span>-₹{totals.totalDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="summary-row grand-total">
                    <span>Grand Total:</span>
                    <span>₹{totals.grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-items">
                <p>No items added yet</p>
                <p className="hint">Search and add items from the left panel</p>
              </div>
            )}

            <div className="action-buttons">
              <button 
                className="save-btn"
                onClick={savePurchase}
                disabled={loading || !!purchaseNumberError || purchaseItems.length === 0 || !selectedSupplier}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Saving...
                  </>
                ) : (
                  isEditMode ? "💾 Update Purchase" : "💾 Save Purchase"
                )}
              </button>
              
              <button className="reset-btn" onClick={resetForm} disabled={loading}>
                🆕 New
              </button>
            </div>
          </div>
        </div>
      </div>

     {/* ================= BOTTOM NAV ================= */}
      <div className="bottom-nav">
        <div onClick={() => navigate("/")} className="nav-item active">
          🏠
          <p>Home</p>
        </div>
        <div onClick={() => navigate("/items")} className="nav-item">
          📦
          <p>Items</p>
        </div>
        <div onClick={() => navigate("/customers")} className="nav-item">
          👥
          <p>Masters</p>
        </div>
        <div onClick={() => navigate("/invoicelist")} className="nav-item">
          🧾
          <p>Invoices</p>
        </div>
      </div>
      <style>{`
        .purchase-page {
          min-height: 100vh;
          background: #f5f5f5;
          padding-bottom: 70px;
        }

        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 15px 20px;
          background: white;
          border-bottom: 1px solid #eee;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        
        .page-header h2 {
          margin: 0;
          font-size: 20px;
          color: #333;
        }

        .header-actions {
          display: flex;
          gap: 10px;
        }

        .back-btn {
          background: #f0f0f0;
          color: #333;
          border: none;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          cursor: pointer;
        }

        .financial-year-bar {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 10px 20px;
          margin: 10px 20px;
          border-radius: 8px;
          display: flex;
          justify-content: space-between;
          font-size: 14px;
        }

        .error-alert {
          background: #ffebee;
          color: #c62828;
          padding: 10px 15px;
          margin: 0 20px 15px;
          border-radius: 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .purchase-container {
          display: grid;
          grid-template-columns: 1fr;
          gap: 20px;
          padding: 0 20px 20px;
        }

        @media (min-width: 1024px) {
          .purchase-container {
            grid-template-columns: 1fr 1.2fr;
          }
        }

        .card {
          background: white;
          border-radius: 12px;
          margin-bottom: 20px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .card h3 {
          margin: 0 0 15px 0;
          font-size: 16px;
          color: #333;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .input-group {
          display: flex;
          gap: 10px;
        }

        .input-group input {
          flex: 1;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }

        .input-group input.error {
          border-color: #ff4444;
        }

        .error-message {
          color: #ff4444;
          font-size: 12px;
          margin-top: 5px;
        }

        .invoice-display {
          display: flex;
          align-items: center;
          background: #f5f5f5;
          border-radius: 8px;
          overflow: hidden;
        }

        .invoice-display span {
          padding: 12px 15px;
          background: #e0e0e0;
          font-weight: bold;
        }

        .invoice-display input {
          flex: 1;
          padding: 12px;
          border: none;
          background: #f5f5f5;
          font-size: 14px;
          outline: none;
        }

        .hint {
          font-size: 11px;
          color: #666;
          margin-top: 5px;
        }

        .date-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }

        .supplier-selector {
          position: relative;
        }

        .search-box input {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }

        .dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          max-height: 300px;
          overflow-y: auto;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          margin-top: 5px;
          z-index: 1000;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .dropdown-item {
          padding: 12px;
          border-bottom: 1px solid #eee;
          cursor: pointer;
        }

        .dropdown-item:hover {
          background: #f5f5f5;
        }

        .mobile {
          color: #666;
          font-size: 12px;
        }

        .supplier-type-badge {
          font-size: 10px;
          color: #5a60ea;
          margin-top: 4px;
        }

        .selected-supplier {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 15px;
          margin-top: 10px;
          border: 1px solid #e0e0e0;
        }

        .supplier-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;
        }

        .change-btn {
          background: #5a60ea;
          color: white;
          border: none;
          padding: 5px 12px;
          border-radius: 15px;
          font-size: 12px;
          cursor: pointer;
        }

        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 50px;
          height: 24px;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.3s;
          border-radius: 24px;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }

        input:checked + .toggle-slider {
          background-color: #5a60ea;
        }

        input:checked + .toggle-slider:before {
          transform: translateX(26px);
        }

        .search-row {
          display: flex;
          gap: 10px;
        }

        .search-row input {
          flex: 1;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }

        .scan-btn {
          padding: 12px 20px;
          background: #5a60ea;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        }

        .item-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          max-height: 300px;
          overflow-y: auto;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          margin-top: 5px;
          z-index: 1000;
        }

        .item-option {
          padding: 12px;
          border-bottom: 1px solid #eee;
          cursor: pointer;
        }

        .item-option:hover {
          background: #f5f5f5;
        }

        .item-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 5px;
        }

        .item-details {
          font-size: 11px;
          color: #666;
        }

        .notes-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          resize: vertical;
        }

        .items-list {
          max-height: 500px;
          overflow-y: auto;
          margin-bottom: 15px;
        }

        .item-row {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 10px;
          border: 1px solid #eee;
        }

        .item-row-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;
        }

        .item-info {
          display: flex;
          gap: 10px;
          align-items: center;
          flex-wrap: wrap;
        }

        .item-name {
          font-weight: bold;
          font-size: 14px;
        }

        .item-hsn {
          font-size: 11px;
          color: #666;
          background: #e0e0e0;
          padding: 2px 8px;
          border-radius: 12px;
        }

        .item-actions {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .remove-btn {
          background: none;
          border: none;
          color: #ff4444;
          font-size: 16px;
          cursor: pointer;
          padding: 0 5px;
        }

        .item-row-details {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
        }

        .quantity-price {
          display: flex;
          align-items: center;
          gap: 5px;
          flex-wrap: wrap;
        }

        .price-input, .qty-input {
          width: 70px;
          padding: 5px;
          border: 1px solid #ddd;
          border-radius: 4px;
          text-align: center;
        }

        .discount-input {
          width: 60px;
          padding: 5px;
          border: 1px solid #ddd;
          border-radius: 4px;
          text-align: center;
        }

        .discount-type {
          padding: 5px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }

        .net-total {
          font-weight: bold;
          color: #5a60ea;
          font-size: 14px;
          min-width: 80px;
          text-align: right;
        }

        .gst-breakup {
          display: flex;
          gap: 10px;
          font-size: 11px;
          color: #666;
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px dashed #ddd;
          flex-wrap: wrap;
        }

        .gst-rate {
          color: #5a60ea;
          font-weight: bold;
        }

        .purchase-summary {
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
          margin-top: 15px;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .summary-row.discount {
          color: #d32f2f;
        }

        .grand-total {
          font-weight: bold;
          font-size: 16px;
          color: #5a60ea;
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px solid #ddd;
        }

        .empty-items {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .action-buttons {
          display: flex;
          gap: 10px;
          margin-top: 20px;
        }

        .save-btn {
          flex: 2;
          padding: 14px;
          background: linear-gradient(135deg, #5a60ea, #6f5bff);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: bold;
          cursor: pointer;
        }

        .save-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .reset-btn {
          flex: 1;
          padding: 14px;
          background: #f0f0f0;
          color: #333;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: bold;
          cursor: pointer;
        }

        .spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid #fff;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .bottom-nav {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
          display: flex;
          justify-content: space-around;
          padding: 8px 0;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
          z-index: 100;
        }

        .bottom-nav div {
          text-align: center;
          cursor: pointer;
          color: #999;
          font-size: 20px;
        }

        .bottom-nav p {
          margin: 2px 0 0;
          font-size: 11px;
        }
      `}</style>
    </div>
  );
}